import * as THREE from "three";
import type { Sight, CameraModeConfig } from "../../types";
import type { CameraConfig } from "../../cameraConfig";
import { defaultCameraConfig } from "../../cameraConfig";
import type { CameraDirector, OrbitAnimationConfig } from "./CameraDirector";
import type { SceneControls } from "../controls/SceneControls";

const FPS_WALKUP_FALLBACK_DISTANCE = 2.0;
const FPS_WALKUP_DIRECTION_EPSILON_SQ = 1e-6;
const _scratchDirFromSight = new THREE.Vector3();
const _scratchCameraForward = new THREE.Vector3();
const _scratchRawPosition = new THREE.Vector3();

/**
 * POI flight duration envelope (seconds), per view mode.
 *
 * Orbit (kaaba/arafat/muzdalifah/overall) — POIs lie in a ≈0.1..1.5-unit
 *   radius; short hops snap in ≈300 ms, long arcs cap at ≈1.2 s. A typical
 *   0.4-unit hop lands at ≈400 ms.
 * FPS (mina/kaaba-inside) — scenes span 7-10 units, walk-up distances can
 *   reach several units. Speed bumped to 4 units/s so a 5-unit cross-scene
 *   move lands at ≈1.25 s without feeling sluggish. Floor stays at 300 ms
 *   for very close POI clicks.
 *
 * Inspired by datum-sdk's `clickNavDurationFor` (`[250, 800] ms` at
 * 12 m/s) — translated to the project's unit scale.
 */
const POI_FLIGHT_MIN_DURATION_S = 0.30;
const POI_FLIGHT_MAX_DURATION_S_ORBIT = 1.20;
const POI_FLIGHT_MAX_SPEED_ORBIT = 1.0;
const POI_FLIGHT_MAX_DURATION_S_FPS = 1.40;
const POI_FLIGHT_MAX_SPEED_FPS = 4.0;

function flightDurationFor(travelUnits: number, isOrbit: boolean): number {
  const speed = isOrbit ? POI_FLIGHT_MAX_SPEED_ORBIT : POI_FLIGHT_MAX_SPEED_FPS;
  const max = isOrbit ? POI_FLIGHT_MAX_DURATION_S_ORBIT : POI_FLIGHT_MAX_DURATION_S_FPS;
  return THREE.MathUtils.clamp(travelUnits / speed, POI_FLIGHT_MIN_DURATION_S, max);
}

/**
 * Manages camera transitions to points of interest (sights) using per-sight camera params
 */
export class SightManager {
  private camera: THREE.PerspectiveCamera;
  private director: CameraDirector;
  private controls: SceneControls;
  private cameraConfig: CameraConfig;
  private activeSight: Sight | null = null;
  private orbitAnimationEnabled: boolean = false;

  constructor(
    camera: THREE.PerspectiveCamera,
    director: CameraDirector,
    controls: SceneControls,
    cameraConfig?: CameraConfig
  ) {
    this.camera = camera;
    this.director = director;
    this.controls = controls;
    this.cameraConfig = cameraConfig ? { ...cameraConfig } : { ...defaultCameraConfig };
  }

  private getCameraSetupForSight(sight: Sight): {
    position: THREE.Vector3;
    target: THREE.Vector3;
    fov?: number;
  } {
    if (!sight.cameraConfig) {
      throw new Error("Sight is missing cameraConfig.");
    }
    const { distance, height = 0, azimuth, polar, fov } = sight.cameraConfig;
    if (distance === undefined || azimuth === undefined || polar === undefined) {
      throw new Error("Sight cameraConfig is missing required params.");
    }

    const x = distance * Math.sin(polar) * Math.cos(azimuth);
    const y = distance * Math.cos(polar) + height;
    const z = distance * Math.sin(polar) * Math.sin(azimuth);

    return {
      position: new THREE.Vector3(
        sight.position.x + x,
        sight.position.y + y,
        sight.position.z + z
      ),
      target: new THREE.Vector3(
        sight.position.x,
        sight.position.y,
        sight.position.z
      ),
      fov,
    };
  }

  /**
   * FPS walk-up pose: approach the sight from the camera's current direction.
   *   - target = sight.position
   *   - position = sight.position + (camera.position - sight.position).normalize() * walkUpDistance
   *     fallback: -cameraForward direction if camera is exactly on the sight
   *   - Y is preserved at camera.position.y (no cumulative drift across POI clicks)
   *   - collision: getSafePosition(sight.position → rawPosition, slide:false) so we
   *     stop before walls instead of ending up inside building/hill OBBs
   */
  private getCameraSetupForSightFps(sight: Sight): {
    position: THREE.Vector3;
    target: THREE.Vector3;
    fov?: number;
  } {
    const walkUpDistance = sight.cameraConfig?.distance ?? FPS_WALKUP_FALLBACK_DISTANCE;

    const sightPos = new THREE.Vector3(sight.position.x, sight.position.y, sight.position.z);

    _scratchDirFromSight.copy(this.camera.position).sub(sightPos);
    if (_scratchDirFromSight.lengthSq() < FPS_WALKUP_DIRECTION_EPSILON_SQ) {
      _scratchCameraForward.set(0, 0, -1).applyQuaternion(this.camera.quaternion);
      _scratchDirFromSight.copy(_scratchCameraForward).negate();
    }
    _scratchDirFromSight.normalize();

    _scratchRawPosition.copy(sightPos).addScaledVector(_scratchDirFromSight, walkUpDistance);
    // Preserve eye level — never let walk-up shift the camera vertically.
    _scratchRawPosition.y = this.camera.position.y;

    const collisionSystem = this.controls.firstPerson.collisionSystem;
    const position = collisionSystem
      ? collisionSystem.getSafePosition(sightPos, _scratchRawPosition, { slide: false }).clone()
      : _scratchRawPosition.clone();

    return {
      position,
      target: sightPos,
      fov: sight.cameraConfig?.fov,
    };
  }

  private getCameraSetupForSightByMode(sight: Sight): {
    position: THREE.Vector3;
    target: THREE.Vector3;
    fov?: number;
  } {
    return this.controls.viewMode === "first-person"
      ? this.getCameraSetupForSightFps(sight)
      : this.getCameraSetupForSight(sight);
  }

  /**
   * Immediately moves the camera to the specified sight using the sight's camera config
   */
  moveToSight(sight: Sight): void {
    this.activeSight = sight;
    const sightPosition = this.getSightPosition(sight);
    const cameraSetup = this.getCameraSetupForSightByMode(sight);
    this.director.moveToPose({
      position: cameraSetup.position,
      target: cameraSetup.target,
      fov: cameraSetup.fov,
    }, false);

    // Orbit-only animation; in FPS the camera is stationary between clicks.
    if (this.controls.viewMode === "orbit" && this.shouldAnimateSight()) {
      this.startOrbitAnimation(sightPosition, sight.cameraConfig);
    } else {
      this.stopOrbitAnimation();
    }
  }

  /**
   * Smoothly transitions the camera to the specified sight using the sight's camera config.
   *
   * Orbit mode (kaaba / arafat / muzdalifah / overall):
   *   - `orbitArc` interpolation — camera sweeps an arc around the lerped
   *     target instead of taking the chord. Trajectory never pierces
   *     `orbitMinDistance`, never bumps Y-clamp, never slides along a wall.
   *   - Vertical constraint OFF (orbit poses freely choose look direction).
   *
   * FPS mode (mina / kaaba-inside):
   *   - `fpsSlerp` interpolation — linear lerp on position + `Quaternion.slerp`
   *     on orientation. The end quaternion is precomputed once from
   *     `(endPosition → endTarget)` with vertical constraint baked in. Slerp
   *     gives a uniform angular speed (subject to easing) regardless of how
   *     far apart `startTarget` (synthetic forward, 1 unit) and `endTarget`
   *     (real POI, often 5–10 units) are. lookAt-lerp instead would
   *     concentrate most of the angular travel into the first ~15% of the
   *     transition and read as a "snap turn of the head".
   *
   * Both modes:
   *   - Duration scaled by travel distance (see `flightDurationFor`).
   *   - Quadratic ease-out — perceptually short tail, controls feel responsive
   *     the moment the camera arrives.
   */
  transitionToSight(
    sight: Sight,
    duration?: number,
    onComplete?: () => void,
    currentTime?: number
  ): void {
    this.activeSight = sight;

    const cameraSetup = this.getCameraSetupForSightByMode(sight);
    const isOrbit = this.controls.viewMode === "orbit";
    const travel = this.camera.position.distanceTo(cameraSetup.position);
    const transitionDuration =
      duration
      ?? sight.transitionDuration
      ?? flightDurationFor(travel, isOrbit);

    this.director.transitionToPose(
      {
        position: cameraSetup.position,
        target: cameraSetup.target,
        fov: cameraSetup.fov,
      },
      transitionDuration,
      () => {
        // Orbit-only animation; in FPS the camera is stationary between clicks.
        if (
          this.controls.viewMode === "orbit" &&
          this.activeSight &&
          this.shouldAnimateSight()
        ) {
          this.startOrbitAnimationForSight(this.activeSight);
        } else {
          this.stopOrbitAnimation();
        }
        onComplete?.();
      },
      currentTime,
      /* applyVerticalConstraint */ !isOrbit,
      /* easing */ 'ease-out',
      /* applyHeightClamp */ true,
      /* interpolation */ isOrbit ? 'orbitArc' : 'fpsSlerp',
    );
  }

  /**
   * Updates the camera transition. Call this in your animation loop.
   */
  update(currentTime: number): void {
    this.director.update(currentTime);
  }

  /**
   * Check if a transition is currently in progress
   */
  isInTransition(): boolean {
    return this.director.isInTransition();
  }

  /**
   * Cancel any ongoing transition
   */
  cancelTransition(): void {
    this.director.cancelTransition();
  }

  startOrbitAnimationForSight(sight: Sight): void {
    const sightPosition = this.getSightPosition(sight);
    this.startOrbitAnimation(sightPosition, sight.cameraConfig);
  }

  setOrbitAnimationEnabled(enabled: boolean): void {
    this.orbitAnimationEnabled = enabled;
    this.director.setOrbitAnimationEnabled(enabled);
  }

  stopOrbitAnimation(): void {
    this.director.stopOrbitAnimation();
  }

  /**
   * Updates the camera configuration used by the manager and reapplies constraints.
   */
  setCameraConfig(config: CameraConfig): void {
    this.cameraConfig = { ...config };
    this.director.setCameraConfig(config);
  }

  private shouldAnimateSight(): boolean {
    return this.orbitAnimationEnabled;
  }

  private startOrbitAnimation(
    sightPosition: THREE.Vector3,
    config?: CameraModeConfig
  ): void {
    const cameraOffset = this.camera.position.clone().sub(sightPosition);
    const spherical = new THREE.Spherical().setFromVector3(cameraOffset);
    const baseAzimuth = Math.atan2(cameraOffset.z, cameraOffset.x);
    const basePolar = spherical.phi;
    const distance = config?.distance ?? cameraOffset.length();
    const height = config?.height ?? 0;
    const azimuthOffsetMin = config?.orbitAzimuthMin ?? -Math.PI / 4;
    const azimuthOffsetMax = config?.orbitAzimuthMax ?? Math.PI / 4;
    const polarOffsetMin = config?.orbitPolarMin ?? -Math.PI / 6;
    const polarOffsetMax = config?.orbitPolarMax ?? Math.PI / 6;
    const azimuthMin = baseAzimuth + azimuthOffsetMin;
    const azimuthMax = baseAzimuth + azimuthOffsetMax;
    const polarMin = basePolar + polarOffsetMin;
    const polarMax = basePolar + polarOffsetMax;
    const polarMinClamped = THREE.MathUtils.clamp(
      Math.min(polarMin, polarMax),
      0,
      Math.PI
    );
    const polarMaxClamped = THREE.MathUtils.clamp(
      Math.max(polarMin, polarMax),
      0,
      Math.PI
    );

    const orbitConfig: OrbitAnimationConfig = {
      sightPosition: sightPosition.clone(),
      distance,
      height,
      azimuthMin: Math.min(azimuthMin, azimuthMax),
      azimuthMax: Math.max(azimuthMin, azimuthMax),
      polarMin: polarMinClamped,
      polarMax: polarMaxClamped,
    };

    this.director.startOrbitAnimation(orbitConfig);
  }

  private getSightPosition(sight: Sight): THREE.Vector3 {
    return new THREE.Vector3(sight.position.x, sight.position.y, sight.position.z);
  }
}
